import { DatePicker, Form, Modal } from "antd"
import React, { useEffect } from "react"
import moment from "moment"
import { TPolicyScheduledPayment } from "models/policies/policyPaymentPlan"
import formatDate from "dateformat"
import { IScheduledPaymentSetDateRequest } from "contexts/opus/context"
import { Nullable } from "models/underwriting"

export const ScheduledPaymentNextRetryDateModal: React.FunctionComponent<IScheduledPaymentNextRetryDate> =
  ({
    scheduledPayment,
    isVisible,
    onCancel,
    onUpdated,
    isUpdating,
    updateScheduledPaymentRetryDate
  }) => {
    const [retryDate, setRetryDate] = React.useState(
      moment(scheduledPayment?.nextRetryDate)
    )

    useEffect(() => {
      setRetryDate(moment(scheduledPayment?.nextRetryDate))
    }, [scheduledPayment?.nextRetryDate])

    const handleCancel = (e: React.MouseEvent<HTMLElement, MouseEvent>) => {
      setRetryDate(moment(scheduledPayment?.nextRetryDate))
      onCancel(e)
    }

    const submitChange = async (newDate: IScheduledPaymentSetDateRequest) => {
      if (scheduledPayment?.id) {
        await updateScheduledPaymentRetryDate(scheduledPayment.id, newDate)
        onUpdated()
      }
    }

    return (
      <Modal
        title={`Set Next Retry Date`}
        centered
        destroyOnClose
        visible={isVisible}
        onCancel={handleCancel}
        okText="Update"
        onOk={() =>
          submitChange({
            newDate: retryDate.format("YYYY-MM-DDTHH:mm:ss") + "Z"
          })
        }
        confirmLoading={isUpdating}
        cancelButtonProps={{ disabled: isUpdating }}
      >
        <p>
          {scheduledPayment &&
            `Override the next retry date for the payment due ${formatDate(
              new Date(scheduledPayment.paymentDate),
              "dd/mm/yyyy"
            )}`}
        </p>
        <Form
          layout="vertical"
          id="scheduledPaymentRetryDateForm"
          style={{ margin: "20px 0 0" }}
        >
          <Form.Item
            name="Next Retry Date"
            label="Next Retry Date:"
            rules={[{ required: true }]}
          >
            <DatePicker
              format={"DD/MM/yyyy"}
              defaultValue={moment(scheduledPayment?.nextRetryDate)}
              value={retryDate}
              onChange={(value) => value && setRetryDate(value.startOf("day"))}
              allowClear={false}
              disabled={isUpdating}
              disabledDate={(current) => {
                if (scheduledPayment?.maxRetryDate) {
                  const minDate = moment
                    .max(moment.utc(), moment(scheduledPayment.paymentDate))
                    .add(1, "days") // can't choose today to prevent changing the retry date to today after the job has already run
                  return !(
                    minDate.isSameOrBefore(current) &&
                    moment(scheduledPayment.maxRetryDate).isSameOrAfter(current)
                  )
                }

                return true
              }}
            />
          </Form.Item>
        </Form>
      </Modal>
    )
  }

export interface IScheduledPaymentNextRetryDate {
  scheduledPayment: Nullable<TPolicyScheduledPayment>
  isVisible: boolean
  onCancel: (e: React.MouseEvent<HTMLElement, MouseEvent>) => void
  onUpdated: () => void
  isUpdating: boolean
  updateScheduledPaymentRetryDate: (
    scheduledPaymentID: string,
    newDate: IScheduledPaymentSetDateRequest
  ) => Promise<void>
}
